import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { toast } from "react-hot-toast";

import {
  Button,
  EditButton,
  IconButton,
  Modal,
  OpenIcon,
  PlusIcon,
  Selector,
  TrashButton,
  type SelectorOption,
} from "../../components/ui";
import { safeArtifactDownloadUrl } from "../../lib/artifactDownload";
import type {
  AgentStoredDocument,
  XLMDocumentLocation,
  XLMDocumentLocator,
  XLMDocumentReference,
} from "../api/e2e";
import { useAgentDocumentLink } from "../api/e2e";
import { useAgentDocumentOpenAction } from "./AgentDocumentActionContext";

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function previewDocument(title: string, content: string): Blob {
  return new Blob(
    [
      `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>`,
      `<style>
        :root{color-scheme:light;font-family:Inter,ui-sans-serif,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;color:#17202e;background:#fff}
        *{box-sizing:border-box}
        body{margin:0;background:#fff;font-size:14px;line-height:1.5}
        main{width:min(1180px,100%);min-height:100vh;margin:0 auto;padding:36px 44px 64px;background:#fff}
        h1,h2,h3,h4{margin:1.4em 0 .55em;color:#111827;line-height:1.25;letter-spacing:-.015em}
        p{margin:.65em 0;color:#344054}
        a{color:#0369a1;text-decoration:none}
        table{max-width:100%;margin:18px auto;border-collapse:separate;border-spacing:0;font-variant-numeric:tabular-nums}
        td,th{padding:7px 10px;border-bottom:1px solid #e5e9ef;color:#273244;vertical-align:top}
        th{color:#111827;background:#f7f9fb;font-weight:650}
        tr:first-child>th,tr:first-child>td{border-top:1px solid #d8dee7}
        tr>th:first-child,tr>td:first-child{border-left:1px solid #d8dee7;border-radius:0}
        tr>th:last-child,tr>td:last-child{border-right:1px solid #d8dee7}
        td:first-child,th:first-child{text-align:left}
        .financial-number{text-align:right;white-space:nowrap}
        td table{width:100%;margin:0;border:0}
        td table td,td table th{padding:4px 7px}
        mark{padding:2px 3px;color:#3b2f00;background:#fff0a8;border-radius:3px;box-shadow:inset 0 -1px 0 #e6b800}
        tr.citation-focus-row>td,tr.citation-focus-row>th{background:#fff9df;border-top-color:#e7c94d;border-bottom-color:#e7c94d}
        tr.citation-focus-row>td:first-child,tr.citation-focus-row>th:first-child{border-left:3px solid #d9a900}
        pre{margin:0;white-space:pre-wrap;word-break:break-word;font:13px/1.65 ui-monospace,SFMono-Regular,Menlo,monospace}
        .image{position:relative;display:inline-block}
        .image img{display:block;max-width:100%;max-height:calc(100vh - 170px)}
        .box{position:absolute;border:3px solid #dc2626;background:rgba(220,38,38,.12);box-sizing:border-box}
        @media(max-width:720px){main{padding:24px 14px}td,th{padding:6px}}
      </style>`,
      `</head><body>${content}<script>document.querySelector('[data-citation-target],.box')?.scrollIntoView({block:'center',inline:'nearest'});</script></body></html>`,
    ],
    { type: "text/html" },
  );
}

function markedText(value: string, start: number, end: number): string {
  const boundedStart = Math.max(0, Math.min(start, value.length));
  const boundedEnd = Math.max(boundedStart, Math.min(end, value.length));
  return `<pre>${escapeHtml(value.slice(0, boundedStart))}<mark>${escapeHtml(value.slice(boundedStart, boundedEnd))}</mark>${escapeHtml(value.slice(boundedEnd))}</pre>`;
}

interface NormalizedTextEntry {
  node: Text;
  text: string;
  normalizedStart: number;
  sourceStart: number;
}

function normalizedTextEntries(document: Document): NormalizedTextEntry[] {
  const root = document.documentElement;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const entries: NormalizedTextEntry[] = [];
  let cursor = 0;
  let node = walker.nextNode();
  while (node) {
    const parent = node.parentElement;
    const source = node.nodeValue ?? "";
    const text = source.trim();
    if (
      parent &&
      !parent.closest("script, style, noscript, template") &&
      text
    ) {
      if (entries.length > 0) cursor += 1;
      entries.push({
        node: node as Text,
        text,
        normalizedStart: cursor,
        sourceStart: source.indexOf(text),
      });
      cursor += text.length;
    }
    node = walker.nextNode();
  }
  return entries;
}

function normalizedHtmlText(entries: NormalizedTextEntry[]): string {
  return entries.map((entry) => entry.text).join(" ");
}

function sanitizeDocument(document: Document): void {
  for (const element of Array.from(document.querySelectorAll("*"))) {
    const tag = element.tagName.toLowerCase();
    const style = element.getAttribute("style")?.toLowerCase() ?? "";
    if (
      [
        "script",
        "style",
        "noscript",
        "template",
        "iframe",
        "object",
        "embed",
        "form",
        "input",
        "button",
        "link",
        "base",
        "svg",
      ].includes(tag) ||
      tag === "ix:hidden" ||
      /display\s*:\s*none/u.test(style)
    ) {
      element.remove();
      continue;
    }
    for (const attribute of Array.from(element.attributes)) {
      if (
        !["colspan", "rowspan", "scope", "title", "hidden"].includes(
          attribute.name.toLowerCase(),
        )
      ) {
        element.removeAttribute(attribute.name);
      }
    }
  }
}

function markedHtmlDocument(
  value: string,
  start: number,
  end: number,
  excerpt: string | null,
  focusTerms: string[],
): string {
  const document = new DOMParser().parseFromString(value, "text/html");
  const entries = normalizedTextEntries(document);
  const visible = normalizedHtmlText(entries);
  let visibleStart = Math.max(0, Math.min(start, visible.length));
  let visibleEnd = Math.max(visibleStart, Math.min(end, visible.length));
  const exactExcerpt = excerpt?.trim();
  if (exactExcerpt) {
    const matches: number[] = [];
    let match = visible.indexOf(exactExcerpt);
    while (match >= 0) {
      matches.push(match);
      match = visible.indexOf(exactExcerpt, match + 1);
    }
    if (matches.length > 0) {
      visibleStart = matches.reduce((closest, candidate) =>
        Math.abs(candidate - visibleStart) < Math.abs(closest - visibleStart)
          ? candidate
          : closest,
      );
      visibleEnd = visibleStart + exactExcerpt.length;
    }
  }
  if (!exactExcerpt || !visible.includes(exactExcerpt)) {
    const boundedText = visible.slice(visibleStart, visibleEnd).toLowerCase();
    for (const rawTerm of focusTerms) {
      const term = rawTerm.trim().toLowerCase();
      if (!term) continue;
      const localMatch = boundedText.indexOf(term);
      if (localMatch < 0) continue;
      const matchStart = visibleStart + localMatch;
      const matchEntry = entries.find(
        (entry) =>
          entry.normalizedStart <= matchStart &&
          entry.normalizedStart + entry.text.length > matchStart,
      );
      const row = matchEntry?.node.parentElement?.closest("tr");
      const rowEntries = row
        ? entries.filter(
            (entry) => entry.node.parentElement?.closest("tr") === row,
          )
        : [];
      if (rowEntries.length > 0) {
        visibleStart = rowEntries[0]!.normalizedStart;
        const lastEntry = rowEntries[rowEntries.length - 1]!;
        visibleEnd = lastEntry.normalizedStart + lastEntry.text.length;
      } else {
        visibleStart = matchStart;
        visibleEnd = matchStart + term.length;
      }
      break;
    }
  }
  for (const entry of entries) {
    const entryEnd = entry.normalizedStart + entry.text.length;
    const overlapStart = Math.max(visibleStart, entry.normalizedStart);
    const overlapEnd = Math.min(visibleEnd, entryEnd);
    if (overlapStart >= overlapEnd || !entry.node.parentNode) continue;
    const localStart = overlapStart - entry.normalizedStart;
    const localEnd = overlapEnd - entry.normalizedStart;
    const source = entry.node.data;
    const before = source.slice(0, entry.sourceStart + localStart);
    const selected = source.slice(
      entry.sourceStart + localStart,
      entry.sourceStart + localEnd,
    );
    const after = source.slice(entry.sourceStart + localEnd);
    const mark = document.createElement("mark");
    mark.textContent = selected;
    entry.node.replaceWith(
      document.createTextNode(before),
      mark,
      document.createTextNode(after),
    );
  }
  sanitizeDocument(document);
  const firstMark = document.querySelector("mark");
  if (firstMark) {
    firstMark.dataset.citationTarget = "true";
    firstMark.closest("tr")?.classList.add("citation-focus-row");
  }
  for (const cell of Array.from(document.querySelectorAll("td, th"))) {
    const text = cell.textContent?.trim() ?? "";
    if (
      !cell.querySelector("table") &&
      /^(?:\(?[-+$€£¥]?\s*\d[\d,.]*%?\)?|[—–-]|\$|\(|\))$/u.test(text)
    ) {
      cell.classList.add("financial-number");
    }
  }
  return `<main>${document.body.innerHTML}</main>`;
}

export function markedWebDocument(
  value: string,
  start: number,
  end: number,
  excerpt: string | null,
  focusTerms: string[] = [],
): string {
  if (!/<(?:!doctype|html|body|head|table|div|p)\b/iu.test(value)) {
    return markedText(value, start, end);
  }
  return markedHtmlDocument(value, start, end, excerpt, focusTerms);
}

function jsonPointerValue(value: unknown, pointer: string): unknown {
  let current = value;
  for (const encoded of pointer.slice(1).split("/")) {
    const token = encoded.replaceAll("~1", "/").replaceAll("~0", "~");
    if (Array.isArray(current)) current = current[Number.parseInt(token, 10)];
    else if (current !== null && typeof current === "object")
      current = (current as Record<string, unknown>)[token];
    else return undefined;
  }
  return current;
}

export async function focusedPreview(
  blob: Blob,
  filename: string,
  locator: XLMDocumentLocator,
  excerpt: string | null,
  focusTerms: string[],
): Promise<{ url: string; ownedUrls: string[] } | null> {
  if (locator.kind === "pdf") {
    const objectUrl = URL.createObjectURL(blob);
    return {
      url: `${objectUrl}#page=${locator.page}&zoom=page-width`,
      ownedUrls: [objectUrl],
    };
  }
  if (locator.kind === "web") {
    const text = await blob.text();
    const url = URL.createObjectURL(
      previewDocument(
        filename,
        markedWebDocument(
          text,
          locator.start,
          locator.end,
          excerpt,
          focusTerms,
        ),
      ),
    );
    return { url, ownedUrls: [url] };
  }
  if (locator.kind === "csv") {
    const text = await blob.text();
    const lines = text.split(/\r?\n/);
    const target = lines[locator.row - 1] ?? "";
    const offset = lines
      .slice(0, locator.row - 1)
      .reduce((total, line) => total + line.length + 1, 0);
    const url = URL.createObjectURL(
      previewDocument(
        filename,
        markedText(text, offset, offset + target.length),
      ),
    );
    return { url, ownedUrls: [url] };
  }
  if (locator.kind === "json") {
    const text = await blob.text();
    let selected: unknown;
    try {
      selected = jsonPointerValue(JSON.parse(text), locator.pointer);
    } catch {
      selected = undefined;
    }
    const selection =
      selected === undefined
        ? "Target not present"
        : JSON.stringify(selected, null, 2);
    const url = URL.createObjectURL(
      previewDocument(
        filename,
        `<h2>Selected value</h2><pre><mark>${escapeHtml(selection ?? "null")}</mark></pre><h2>Document</h2><pre>${escapeHtml(text)}</pre>`,
      ),
    );
    return { url, ownedUrls: [url] };
  }
  if (locator.kind === "image") {
    const imageUrl = URL.createObjectURL(blob);
    const box = locator.bounding_box;
    const content = `<div class="image"><img src="${imageUrl}" alt="${escapeHtml(filename)}"><span class="box" style="left:${box.left * 100}%;top:${box.top * 100}%;width:${(box.right - box.left) * 100}%;height:${(box.bottom - box.top) * 100}%"></span></div>`;
    const url = URL.createObjectURL(previewDocument(filename, content));
    return { url, ownedUrls: [url, imageUrl] };
  }
  return null;
}

export function locatorPreviewSupport(
  locator: XLMDocumentLocator,
): "focused" | "download" {
  return ["pdf", "image", "web", "csv", "json"].includes(locator.kind)
    ? "focused"
    : "download";
}

export function xlmLocatorLabel(locator: XLMDocumentLocator): string {
  switch (locator.kind) {
    case "xlsx":
      return `${locator.sheet}!${locator.cell_range}`;
    case "pdf":
      return `Page ${locator.page}${locator.table ? ` · table ${locator.table}` : ""}`;
    case "pptx":
      return `Slide ${locator.slide}${locator.shape ? ` · shape ${locator.shape}` : ""}`;
    case "docx":
      return locator.paragraph
        ? `Paragraph ${locator.paragraph}`
        : `Table ${locator.table} · row ${locator.row} · column ${locator.column}`;
    case "csv":
      return `Row ${locator.row} · column ${locator.column}`;
    case "json":
      return `JSON pointer ${locator.pointer}`;
    case "image":
      return `${locator.page ? `Page ${locator.page} · ` : ""}bounded image region`;
    case "web":
      return `Captured text ${locator.start}–${locator.end}`;
  }
}

function locatorKind(
  document: AgentStoredDocument,
): XLMDocumentLocator["kind"] {
  const name = document.filename.toLowerCase();
  if (name.endsWith(".xlsx")) return "xlsx";
  if (name.endsWith(".pdf")) return "pdf";
  if (name.endsWith(".pptx")) return "pptx";
  if (name.endsWith(".docx")) return "docx";
  if (name.endsWith(".csv")) return "csv";
  if (name.endsWith(".json")) return "json";
  if (document.media_type.startsWith("image/")) return "image";
  return "web";
}

function defaultLocator(kind: XLMDocumentLocator["kind"]): XLMDocumentLocator {
  switch (kind) {
    case "xlsx":
      return { kind, sheet: "Sheet1", cell_range: "A1" };
    case "pdf":
      return { kind, page: 1, table: null, bounding_box: null };
    case "pptx":
      return {
        kind,
        slide: 1,
        shape: null,
        table: null,
        row: null,
        column: null,
      };
    case "docx":
      return { kind, paragraph: 1, table: null, row: null, column: null };
    case "csv":
      return { kind, row: 1, column: 1 };
    case "json":
      return { kind, pointer: "/" };
    case "image":
      return {
        kind,
        page: null,
        bounding_box: { left: 0, top: 0, right: 1, bottom: 1 },
      };
    case "web":
      return { kind, start: 0, end: 1 };
  }
}

export function documentPreviewLocator(
  document: AgentStoredDocument,
): XLMDocumentLocator {
  return defaultLocator(locatorKind(document));
}

function numberValue(value: string, fallback = 1): number {
  const number = Number.parseInt(value, 10);
  return Number.isFinite(number) ? number : fallback;
}

function LocatorField({
  label,
  wide = false,
  children,
}: {
  label: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <label className={`xlm-document-location__field${wide ? " is-wide" : ""}`}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function LocatorFields({
  locator,
  onChange,
}: {
  locator: XLMDocumentLocator;
  onChange: (value: XLMDocumentLocator) => void;
}) {
  if (locator.kind === "xlsx")
    return (
      <>
        <LocatorField label="Sheet">
          <input
            aria-label="Sheet"
            value={locator.sheet}
            onChange={(event) =>
              onChange({ ...locator, sheet: event.target.value })
            }
          />
        </LocatorField>
        <LocatorField label="Cell range">
          <input
            aria-label="Cell range"
            value={locator.cell_range}
            onChange={(event) =>
              onChange({ ...locator, cell_range: event.target.value })
            }
          />
        </LocatorField>
      </>
    );
  if (locator.kind === "pdf")
    return (
      <>
        <LocatorField label="Page">
          <input
            aria-label="Page"
            type="number"
            min={1}
            value={locator.page}
            onChange={(event) =>
              onChange({ ...locator, page: numberValue(event.target.value) })
            }
          />
        </LocatorField>
        <LocatorField label="Table (optional)">
          <input
            aria-label="Table"
            value={locator.table ?? ""}
            onChange={(event) =>
              onChange({ ...locator, table: event.target.value || null })
            }
          />
        </LocatorField>
      </>
    );
  if (locator.kind === "pptx")
    return (
      <>
        <LocatorField label="Slide">
          <input
            aria-label="Slide"
            type="number"
            min={1}
            value={locator.slide}
            onChange={(event) =>
              onChange({ ...locator, slide: numberValue(event.target.value) })
            }
          />
        </LocatorField>
        <LocatorField label="Shape (optional)">
          <input
            aria-label="Shape"
            value={locator.shape ?? ""}
            onChange={(event) =>
              onChange({ ...locator, shape: event.target.value || null })
            }
          />
        </LocatorField>
      </>
    );
  if (locator.kind === "docx")
    return (
      <LocatorField label="Paragraph" wide>
        <input
          aria-label="Paragraph"
          type="number"
          min={1}
          value={locator.paragraph ?? 1}
          onChange={(event) =>
            onChange({
              kind: "docx",
              paragraph: numberValue(event.target.value),
              table: null,
              row: null,
              column: null,
            })
          }
        />
      </LocatorField>
    );
  if (locator.kind === "csv")
    return (
      <>
        <LocatorField label="Row">
          <input
            aria-label="Row"
            type="number"
            min={1}
            value={locator.row}
            onChange={(event) =>
              onChange({ ...locator, row: numberValue(event.target.value) })
            }
          />
        </LocatorField>
        <LocatorField label="Column">
          <input
            aria-label="Column"
            value={locator.column}
            onChange={(event) =>
              onChange({ ...locator, column: event.target.value || 1 })
            }
          />
        </LocatorField>
      </>
    );
  if (locator.kind === "json")
    return (
      <LocatorField label="JSON pointer" wide>
        <input
          aria-label="JSON pointer"
          value={locator.pointer}
          onChange={(event) =>
            onChange({ ...locator, pointer: event.target.value })
          }
        />
      </LocatorField>
    );
  if (locator.kind === "web")
    return (
      <>
        <LocatorField label="Text start">
          <input
            aria-label="Text start"
            type="number"
            min={0}
            value={locator.start}
            onChange={(event) =>
              onChange({
                ...locator,
                start: numberValue(event.target.value, 0),
              })
            }
          />
        </LocatorField>
        <LocatorField label="Text end">
          <input
            aria-label="Text end"
            type="number"
            min={1}
            value={locator.end}
            onChange={(event) =>
              onChange({ ...locator, end: numberValue(event.target.value) })
            }
          />
        </LocatorField>
      </>
    );
  return (
    <div className="xlm-bounding-box-fields">
      {(["left", "top", "right", "bottom"] as const).map((field) => (
        <LocatorField
          key={field}
          label={`${field[0]?.toLocaleUpperCase()}${field.slice(1)}`}
        >
          <input
            aria-label={field}
            type="number"
            min={0}
            max={1}
            step={0.01}
            value={locator.bounding_box[field]}
            onChange={(event) =>
              onChange({
                ...locator,
                bounding_box: {
                  ...locator.bounding_box,
                  [field]: Number(event.target.value),
                },
              })
            }
          />
        </LocatorField>
      ))}
    </div>
  );
}

interface DocumentPreviewIdentity {
  name: string;
  filename: string;
  releaseDate: string | null;
  locatorLabel: string;
  sourceUrl: string | null;
}

type DocumentPreviewState =
  | { kind: "idle" }
  | (DocumentPreviewIdentity & {
      kind: "loading";
    })
  | (DocumentPreviewIdentity & {
      kind: "ready";
      url: string;
      blob: Blob;
      ownedUrls: string[];
    })
  | (DocumentPreviewIdentity & {
      kind: "download";
      blob: Blob;
    })
  | (DocumentPreviewIdentity & {
      kind: "external";
      url: string;
    })
  | (DocumentPreviewIdentity & {
      kind: "error";
      message: string;
    });

type LocatorKind = XLMDocumentLocator["kind"];

const SOURCE_TYPE_OPTIONS: readonly SelectorOption<LocatorKind>[] = [
  { value: "xlsx", label: "XLSX" },
  { value: "pdf", label: "PDF" },
  { value: "pptx", label: "PPTX" },
  { value: "docx", label: "DOCX" },
  { value: "csv", label: "CSV" },
  { value: "json", label: "JSON" },
  { value: "image", label: "Image" },
  { value: "web", label: "Web" },
];

export function XLMDocumentEvidence({
  companyId,
  documents,
  references,
  focusTerms,
  editing,
  onChange,
}: {
  companyId: string;
  documents: AgentStoredDocument[];
  references: XLMDocumentReference[];
  focusTerms?: string[];
  editing: boolean;
  onChange: (value: XLMDocumentReference[]) => void;
}) {
  const link = useAgentDocumentLink();
  const hostOpenDocument = useAgentDocumentOpenAction();
  const [selectedDocument, setSelectedDocument] = useState("");
  const [addingDocument, setAddingDocument] = useState(false);
  const [editingDocumentId, setEditingDocumentId] = useState<string | null>(
    null,
  );
  const [preview, setPreview] = useState<DocumentPreviewState>({
    kind: "idle",
  });
  const previewRequest = useRef(0);
  const byId = new Map(documents.map((document) => [document.id, document]));
  const availableDocuments = useMemo(
    () =>
      documents.filter(
        (document) =>
          !references.some(
            (reference) => reference.document_id === document.id,
          ),
      ),
    [documents, references],
  );
  const documentOptions = useMemo<readonly SelectorOption<string>[]>(
    () =>
      availableDocuments.map((document) => ({
        value: document.id,
        label: `${document.name} — ${document.filename}`,
      })),
    [availableDocuments],
  );

  useEffect(() => {
    if (
      editingDocumentId &&
      !references.some(
        (reference) => reference.document_id === editingDocumentId,
      )
    ) {
      setEditingDocumentId(null);
    }
  }, [editingDocumentId, references]);

  useEffect(
    () => () => {
      if (preview.kind === "ready") {
        for (const url of preview.ownedUrls) URL.revokeObjectURL(url);
      }
    },
    [preview],
  );

  const previewSource = async (
    reference: XLMDocumentReference,
    location: XLMDocumentLocation,
  ) => {
    const document = byId.get(reference.document_id);
    if (!document) return;
    if (hostOpenDocument) {
      try {
        await hostOpenDocument(document);
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "The document could not be opened.",
        );
      }
      return;
    }
    const request = ++previewRequest.current;
    const locatorLabel = xlmLocatorLabel(location.locator);
    const identity: DocumentPreviewIdentity = {
      name: document.name,
      filename: document.filename,
      releaseDate: document.release_date,
      locatorLabel,
      sourceUrl: document.source_url,
    };
    setPreview({
      kind: "loading",
      ...identity,
    });
    try {
      const resolved = await link.mutateAsync({
        companyId,
        documentId: document.id,
      });
      if (request !== previewRequest.current) return;
      let response: Response;
      if (resolved.preview_token === null) {
        const originalUrl = safeArtifactDownloadUrl(
          resolved.url,
          window.location.origin,
        );
        if (!import.meta.env.DEV) {
          setPreview({
            kind: "external",
            ...identity,
            url: originalUrl,
          });
          return;
        }
        response = await fetch("/__document-preview", {
          method: "POST",
          body: originalUrl,
          credentials: "omit",
          referrerPolicy: "no-referrer",
        });
      } else {
        const previewPath = `/api/v1/document-capabilities/${encodeURIComponent(companyId)}/${encodeURIComponent(document.id)}`;
        response = await fetch(previewPath, {
          credentials: "omit",
          referrerPolicy: "no-referrer",
          headers: { "X-Document-Capability": resolved.preview_token },
        });
      }
      if (!response.ok) throw new Error("Source download failed.");
      const blob = await response.blob();
      const focused = await focusedPreview(
        blob,
        document.filename,
        location.locator,
        location.excerpt ?? null,
        focusTerms ?? [],
      );
      if (request !== previewRequest.current) {
        if (focused) {
          for (const url of focused.ownedUrls) URL.revokeObjectURL(url);
        }
        return;
      }
      if (focused) {
        setPreview({
          kind: "ready",
          ...identity,
          url: focused.url,
          blob,
          ownedUrls: focused.ownedUrls,
        });
      } else {
        setPreview({
          kind: "download",
          ...identity,
          blob,
        });
      }
    } catch (error) {
      if (request !== previewRequest.current) return;
      const message =
        error instanceof Error ? error.message : "Source could not be opened.";
      setPreview({
        kind: "error",
        ...identity,
        message,
      });
      toast.error(message);
    }
  };

  const closePreview = () => {
    previewRequest.current += 1;
    setPreview({ kind: "idle" });
  };

  return (
    <div
      className={`xlm-document-evidence${editing ? " is-editable" : ""}${
        editingDocumentId ? " has-open-editor" : ""
      }`}
    >
      <header className="xlm-document-evidence__header">
        <div>
          <h3>Source evidence</h3>
          <span>{references.length}</span>
        </div>
        {editing && (
          <IconButton
            tone="quiet"
            size="xs"
            className="xlm-document-evidence__add"
            label={addingDocument ? "Close source picker" : "Add source"}
            tooltip={
              availableDocuments.length === 0
                ? "Every company document is already attached."
                : addingDocument
                  ? "Close the source picker."
                  : "Attach a company document."
            }
            icon={<PlusIcon />}
            disabled={availableDocuments.length === 0}
            aria-expanded={addingDocument}
            aria-pressed={addingDocument}
            onClick={() => {
              setAddingDocument((current) => !current);
              setSelectedDocument("");
            }}
          />
        )}
      </header>
      <div className="xlm-document-reference-list">
        {references.length === 0 && (
          <p className="xlm-document-evidence__empty">
            No source evidence attached.
          </p>
        )}
        {references.map((reference, referenceIndex) => {
          const document = byId.get(reference.document_id);
          const name = document?.name ?? reference.document_id;
          const filename = document?.filename ?? reference.document_id;
          const referenceEditing =
            editing && editingDocumentId === reference.document_id;
          return (
            <section
              key={reference.document_id}
              className={`xlm-document-reference${
                referenceEditing ? " is-editing" : ""
              }`}
            >
              <header>
                <div className="xlm-document-reference__identity">
                  <strong title={name}>{name}</strong>
                  {document && <small title={filename}>{filename}</small>}
                </div>
                {editing && (
                  <div className="xlm-document-reference__header-actions">
                    <EditButton
                      label={`${referenceEditing ? "Finish editing" : "Edit"} ${name}`}
                      aria-pressed={referenceEditing}
                      onClick={() =>
                        setEditingDocumentId((current) =>
                          current === reference.document_id
                            ? null
                            : reference.document_id,
                        )
                      }
                    />
                    <TrashButton
                      label={`Delete source ${name}`}
                      onClick={() => {
                        if (editingDocumentId === reference.document_id) {
                          setEditingDocumentId(null);
                        }
                        onChange(
                          references.filter(
                            (_, index) => index !== referenceIndex,
                          ),
                        );
                      }}
                    />
                  </div>
                )}
              </header>
              {referenceEditing ? (
                <label className="xlm-document-reference__evidence-field">
                  <span>Evidence note</span>
                  <textarea
                    className="xlm-document-reference__evidence-editor"
                    aria-label="Reference evidence"
                    placeholder="Optional support note"
                    maxLength={4000}
                    rows={2}
                    value={reference.evidence ?? ""}
                    onChange={(event) => {
                      const next = structuredClone(references);
                      next[referenceIndex]!.evidence =
                        event.target.value || null;
                      onChange(next);
                    }}
                  />
                </label>
              ) : (
                reference.evidence && (
                  <p className="xlm-document-reference__evidence">
                    <strong>Evidence</strong>
                    <span>{reference.evidence}</span>
                  </p>
                )
              )}
              {reference.locations.map((location, locationIndex) => (
                <div
                  key={`${location.locator.kind}:${locationIndex}`}
                  className="xlm-document-location"
                >
                  {referenceEditing ? (
                    <>
                      <div className="xlm-document-location__fields">
                        <LocatorField label="Source type" wide>
                          <Selector
                            options={SOURCE_TYPE_OPTIONS}
                            value={location.locator.kind}
                            ariaLabel="Locator type"
                            onValueChange={(kind) => {
                              const next = structuredClone(references);
                              next[referenceIndex]!.locations[
                                locationIndex
                              ]!.locator = defaultLocator(kind);
                              onChange(next);
                            }}
                          />
                        </LocatorField>
                        <LocatorFields
                          locator={location.locator}
                          onChange={(locator) => {
                            const next = structuredClone(references);
                            next[referenceIndex]!.locations[
                              locationIndex
                            ]!.locator = locator;
                            onChange(next);
                          }}
                        />
                        <LocatorField label="Source excerpt" wide>
                          <textarea
                            aria-label="Source excerpt"
                            placeholder="Optional excerpt"
                            rows={2}
                            value={location.excerpt ?? ""}
                            onChange={(event) => {
                              const next = structuredClone(references);
                              next[referenceIndex]!.locations[
                                locationIndex
                              ]!.excerpt = event.target.value || null;
                              onChange(next);
                            }}
                          />
                        </LocatorField>
                      </div>
                      <footer className="xlm-document-location__actions">
                        <Button
                          type="button"
                          tone="quiet"
                          size="xs"
                          aria-label="Remove source location"
                          onClick={() => {
                            const next = structuredClone(references);
                            next[referenceIndex]!.locations.splice(
                              locationIndex,
                              1,
                            );
                            if (next[referenceIndex]!.locations.length === 0)
                              next.splice(referenceIndex, 1);
                            onChange(next);
                          }}
                        >
                          Remove location
                        </Button>
                      </footer>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="xlm-document-location__preview"
                        onClick={() => void previewSource(reference, location)}
                      >
                        <span>{xlmLocatorLabel(location.locator)}</span>
                        <small>
                          {locatorPreviewSupport(location.locator) === "focused"
                            ? "Preview cited position"
                            : "Show reference and download"}
                        </small>
                      </button>
                      {location.excerpt && <q>{location.excerpt}</q>}
                    </>
                  )}
                </div>
              ))}
              {referenceEditing && (
                <footer className="xlm-document-reference__actions">
                  <Button
                    type="button"
                    tone="quiet"
                    size="xs"
                    disabled={!document}
                    onClick={() => {
                      if (!document) return;
                      const next = structuredClone(references);
                      const kind = locatorKind(document);
                      next[referenceIndex]!.locations.push({
                        locator: defaultLocator(kind),
                        excerpt: null,
                      });
                      onChange(next);
                    }}
                  >
                    Add location
                  </Button>
                </footer>
              )}
            </section>
          );
        })}
        {editing && addingDocument && (
          <div className="xlm-add-document">
            <Selector
              options={documentOptions}
              value={selectedDocument}
              onValueChange={setSelectedDocument}
              ariaLabel="Company document"
              placeholder="Select company document…"
              searchable
              portalMenu
            />
            <Button
              type="button"
              tone="secondary"
              size="xs"
              disabled={!selectedDocument}
              onClick={() => {
                const document = byId.get(selectedDocument);
                if (!document) return;
                onChange([
                  ...references,
                  {
                    document_id: document.id,
                    evidence: null,
                    locations: [
                      {
                        locator: defaultLocator(locatorKind(document)),
                        excerpt: null,
                      },
                    ],
                  },
                ]);
                setSelectedDocument("");
                setAddingDocument(false);
                setEditingDocumentId(document.id);
              }}
            >
              Attach source
            </Button>
          </div>
        )}
      </div>

      {preview.kind !== "idle" && (
        <Modal
          size="preview"
          className="xlm-document-modal"
          ariaLabelledBy="xlm-document-modal-title"
          onClose={closePreview}
        >
          <header>
            <div>
              <span className="eyebrow">Cited document</span>
              <strong id="xlm-document-modal-title">{preview.name}</strong>
              <small>
                {preview.filename}
                {preview.releaseDate ? ` · ${preview.releaseDate}` : ""}
              </small>
              <small>{preview.locatorLabel}</small>
            </div>
            <div className="xlm-document-modal__actions">
              {preview.sourceUrl && (
                <a href={preview.sourceUrl} target="_blank" rel="noreferrer">
                  Open source URL <OpenIcon />
                </a>
              )}
              {(preview.kind === "ready" ||
                preview.kind === "download" ||
                preview.kind === "external") && (
                <>
                  {(preview.kind === "ready" ||
                    preview.kind === "external") && (
                    <button
                      type="button"
                      onClick={() =>
                        window.open(preview.url, "_blank", "noopener")
                      }
                    >
                      {preview.kind === "external"
                        ? "Open original"
                        : "Open separately"}
                      <OpenIcon />
                    </button>
                  )}
                  {(preview.kind === "ready" ||
                    preview.kind === "download") && (
                    <button
                      type="button"
                      onClick={() => saveBlob(preview.blob, preview.filename)}
                    >
                      Download
                    </button>
                  )}
                </>
              )}
              <button
                type="button"
                aria-label="Close cited document"
                onClick={closePreview}
              >
                ×
              </button>
            </div>
          </header>
          <div className="xlm-document-modal__body" aria-live="polite">
            {preview.kind === "loading" && (
              <div className="xlm-document-preview__state" role="status">
                Loading cited position…
              </div>
            )}
            {preview.kind === "error" && (
              <div className="xlm-document-preview__state" role="alert">
                {preview.message}
              </div>
            )}
            {preview.kind === "download" && (
              <div className="xlm-document-preview__state">
                <strong>
                  This format has no safe inline browser renderer.
                </strong>
                <p>
                  The exact document coordinate is shown above. Download the
                  original to inspect that position in its native application.
                </p>
              </div>
            )}
            {preview.kind === "external" && (
              <div className="xlm-document-preview__state">
                <strong>The inline preview service is unavailable.</strong>
                <p>Open the original document to inspect this citation.</p>
              </div>
            )}
            {preview.kind === "ready" && (
              <iframe
                className="xlm-document-preview__frame"
                src={preview.url}
                title={`${preview.name}, file ${preview.filename}, at ${preview.locatorLabel}`}
              />
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
